import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslate } from '@bynder/localization';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import ProtectedPage from 'packages/pages/components/ProtectedPage';
import { currentTemplateSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import permissions from 'packages/configs/permissions';
import { PageWithAppcues } from 'packages/helpers/RouteWithAppcues';
import authorizationHelper from '~/helpers/AuthorizationHelper';
import CreativeEditor from './CreativeEditor';
import { colorPalettesFetching } from 'packages/store/brandColors/brandColors.selectors';
import { getColorPalettes } from 'packages/store/brandColors/colorPalettes.actions';
import { fetchCreative } from '~/store/creatives/common.actions';
import { getCreativeTemplate, resetEditor } from 'packages/store/creativeEditor/creativeEditor.actions';
import { initReview } from 'packages/store/review/review.actions';
import { useRegisterLoadingTask } from 'packages/common/LoadingBar';
import { getCreative } from 'packages/store/creatives/creatives.selectors';

export default function TemplateEditor() {
    const dispatch = useThunkDispatch();
    const params = useParams<{ creativeId: string }>();
    const { translate } = useTranslate();
    const template = useSelector(currentTemplateSelector);
    const creative = useSelector(getCreative);
    const isFetchingPalettes = useSelector(colorPalettesFetching);

    useEffect(() => {
        const id = params.creativeId;

        dispatch(fetchCreative(id, true));
        dispatch(getCreativeTemplate(id, false, 'creative'));
        dispatch(getColorPalettes());

        return () => {
            dispatch(resetEditor());
            dispatch(initReview()); // todo: is it necessary here?
        };
    }, [dispatch, params.creativeId]);

    useRegisterLoadingTask('creative', Boolean(creative));
    useRegisterLoadingTask('template', Boolean(template), { weight: 2 });
    useRegisterLoadingTask('palettes', !isFetchingPalettes);

    const isLoaded = Boolean(template && creative && !isFetchingPalettes);

    if (!isLoaded) {
        return null;
    }

    const hasEditPermission = authorizationHelper.hasPermissions([permissions.TEMPLATES.WRITE]);

    const getTitle = () => {
        if (!hasEditPermission) {
            // todo: is this the correct title?
            return translate('editor.approvals.locked.title');
        }
    };

    const getDescription = () => {
        if (!hasEditPermission) {
            return translate('editor.no-access.desc');
        }
    };

    return (
        <PageWithAppcues>
            <ProtectedPage
                isAllowed={hasEditPermission}
                redirectPath="/categories"
                title={getTitle()}
                description={getDescription()}
            >
                <CreativeEditor />
            </ProtectedPage>
        </PageWithAppcues>
    );
}
